<template>
  <div>
    <div v-bind='$attrs'>
      <label
        v-if='label'
        data-cy='label'
        class='block text-sm'
        :class='[(disabled && editMode) ? "text-gray-400" : "text-gray-700"]'
      >
        <QuestionMarkCircleIcon
          v-if='help'
          class='question-mark-circle-icon mb-1 mr-0.5 inline-block h-4 w-4 cursor-pointer'
          @click='setHelpModalVisibility(true)'
        />
        <span
          :title='label'
          v-html='getLabel()'
        />
      </label>
      <template v-if='editMode'>
        <v-ace-editor
          v-model:value='content'
          data-cy='code-editor'
          :class='[ disabled && "bg-gray-50 text-gray-400"]'
          :model-value='content'
          :lang='lang'
          :mode='mode'
          :readonly='editMode && disabled'
          theme='chrome'
          :options='options'
          :auto-height='autoHeight'
          :style='style'
          @input='updateValue(content)'
        />
        <InputError :error='error'/>
      </template>
      <template v-else>
        <div class='text-sm'>
          <v-ace-editor
            v-model:value='content'
            data-cy='code-editor'
            :model-value='content'
            :class='[ disabled && "bg-gray-50 text-gray-400"]'
            :mode='mode'
            :lang='lang'
            theme='chrome'
            :style='style'
            :readonly='!editMode'
          />
        </div>
      </template>
    </div>
    <Modal
      :open='helpVisible'
      :title='label'
      @close-modal='setHelpModalVisibility(false)'
    >
      <template #content>
        <p v-html='help'/>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, watch } from 'vue'
  import type { Ref } from 'vue'
  import useInputFieldBase, { InputFieldProps,
                              InputFieldComponents,
                              InputFieldEmits } from '../../../compositions/useInputFieldBase'
  import useInputFieldHasHelp, { InputFieldHasHelpProps,
                                 InputFieldHasHelpComponents } from '../../../compositions/useInputFieldHasHelp'

  import { VAceEditor } from 'vue3-ace-editor'
  import 'ace-builds/src-noconflict/theme-chrome'
  import 'ace-builds/src-noconflict/mode-json'
  import 'ace-builds/src-noconflict/mode-html'
  import extSearchboxUrl from 'ace-builds/src-noconflict/ext-searchbox?url'
  import ace from 'ace-builds/src-noconflict/ace'

  export default defineComponent({
    name: 'CodeField',
    components: {
      ...InputFieldComponents,
      ...InputFieldHasHelpComponents,
      VAceEditor,
    },
    props: {
      ...InputFieldProps,
      ...InputFieldHasHelpProps,
      mode: {
        type: String,
        default: 'json',
      },
      lang: {
        type: String,
        default: 'json',
      },
      withIcon: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
        default: null,
      },
      error: {
        type: Array,
        default: () => [],
      },
      options: {
        type: Object,
        default: null,
      },
      autoHeight: {
        type: Boolean,
        default: false,
      },
      style: {
        type: Object,
        default: null,
      },
    },
    emits: [
      ...InputFieldEmits,
      'has-focus',
    ],
    setup(props) {
      ace.config.setModuleUrl('ace/ext/searchbox', extSearchboxUrl)

      const {
        helpVisible,
        setHelpModalVisibility,
      } = useInputFieldHasHelp()
      const {
        updateValue: emitValue,
        getLabel,
      } = useInputFieldBase()

      const updateValue = (value: string) => {
        if(!props.disabled) {
          emitValue(value)
        }
      }
      const content: Ref<string> = ref(props.modelValue ?? '')

      watch(() => props.modelValue, (newValue) => {
        if (!newValue) content.value = ''
        else content.value = newValue
      })

      return {
        updateValue,
        getLabel,
        helpVisible,
        setHelpModalVisibility,
        content,
      }
    },
  })
</script>
